/**
 * The one field in this form the CMS deliberately will not write.
 *
 * Everything else an editor used to type is now derived from something they
 * already wrote. The direct answer is not, and that is a decision rather than
 * an omission. It is rendered under the H1 for readers and exposed as the
 * article abstract in JSON-LD, which means it is the sentence a search engine
 * or an assistant is most likely to quote back as Zennopay's own statement.
 * Zennopay is a registered MSB. A generated answer would be an invented factual
 * claim published under a licensed brand, and no amount of "the editor could
 * have checked it" makes that a good trade.
 *
 * So the help this field gives is the honest kind: it counts, live, against the
 * band the publish gate enforces, and says why nobody is writing it for you.
 * Hitting 40 to 70 words should be a glance, not a guess followed by a rejected
 * publish.
 */

import React from "react";
import { FieldHelperText, FieldProps } from "@firecms/core";
import { DebouncedTextField, Typography } from "@firecms/ui";

import {
    countWords,
    DIRECT_ANSWER_MAX_WORDS,
    DIRECT_ANSWER_MIN_WORDS
} from "../derive/text";

/** Where the count stands against the gate's band, and what to say about it. */
function verdict(words: number): { tone: string; message: string } {
    if (words === 0) {
        return {
            tone: "opacity-70",
            message: `${DIRECT_ANSWER_MIN_WORDS} to ${DIRECT_ANSWER_MAX_WORDS} words. `
                + "Answer the primary question so the sentence stands alone, out of context."
        };
    }
    if (words < DIRECT_ANSWER_MIN_WORDS) {
        const missing = DIRECT_ANSWER_MIN_WORDS - words;
        return {
            tone: "text-amber-600",
            message: `${missing} more word${missing === 1 ? "" : "s"} to reach ${DIRECT_ANSWER_MIN_WORDS}.`
        };
    }
    if (words > DIRECT_ANSWER_MAX_WORDS) {
        const over = words - DIRECT_ANSWER_MAX_WORDS;
        return {
            tone: "text-red-500",
            message: `${over} word${over === 1 ? "" : "s"} over ${DIRECT_ANSWER_MAX_WORDS}. `
                + "Publishing is blocked until it is inside the band."
        };
    }
    return { tone: "text-green-600", message: "Inside the band." };
}

export function DirectAnswerField({
    property,
    value,
    setValue,
    error,
    showError,
    disabled,
    includeDescription
}: FieldProps<string>) {

    const words = countWords(value ?? "");
    const { tone, message } = verdict(words);

    return (
        <div className={"flex flex-col gap-1"}>
            <DebouncedTextField
                value={value ?? ""}
                label={property.name}
                multiline={true}
                minRows={4}
                disabled={disabled ?? false}
                error={Boolean(showError && error)}
                onChange={(event) => setValue(event.target.value)}/>

            <div className={"flex flex-row gap-2 items-baseline flex-wrap"}>
                <Typography variant={"body2"} className={`font-mono ${tone}`}>
                    {words}/{DIRECT_ANSWER_MIN_WORDS}–{DIRECT_ANSWER_MAX_WORDS} words
                </Typography>
                <Typography variant={"caption"} className={tone}>
                    {message}
                </Typography>
            </div>

            <Typography variant={"caption"} className={"opacity-70"}>
                You write this one. It is rendered under the heading and published as this
                guide&rsquo;s abstract in JSON-LD, so search engines and assistants quote it back
                as a Zennopay statement of fact. Zennopay is a registered MSB, so the CMS will
                not draft it for you. The excerpt, meta description, and card summary are all
                taken from it once it is written.
            </Typography>

            <FieldHelperText error={error}
                             showError={showError ?? false}
                             includeDescription={includeDescription ?? true}
                             property={property}/>
        </div>
    );
}
